import React, {Fragment, memo, useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import Link from 'next/link';
import {useRouter} from 'next/router';
import {cdnUrl} from '@/util/cdnImage';
import type {NavMenuGroup, NavSubmenuItem} from '@/types/navigation';
import DropdownCta from './DropdownCta';
import {CARD_COLUMNS, distributeIntoColumns, renderCategoryColumns} from './CategoryCards';
import {minWidthQuery, NAV_DESKTOP_MIN} from '@/styles/breakpoints';
import Icon from '@/components/atoms/Icon';
import useMatchMedia from '@/components/plain/useMatchMedia';
import styles from './PopNavMenu.module.scss';

const cx = (...classes: (string | false | undefined | null)[]) => classes.filter(Boolean).join(' ');

// useLayoutEffect warns during SSR; the dropdown never renders on the server anyway.
const useIsomorphicLayoutEffect = typeof globalThis.window === 'undefined' ? useEffect : useLayoutEffect;

const NAV_ICON_DIR = 'ai-development-company/home-navigation-icons';

const SIDEBAR_GROUP_ICONS: Record<string, string> = {
    'Software Development': cdnUrl(`${NAV_ICON_DIR}/software-dev.svg`),
    'Mobile App Development': cdnUrl(`${NAV_ICON_DIR}/mobile-app-dev.svg`),
    'AI Development': cdnUrl(`${NAV_ICON_DIR}/ai-dev.svg`),
    'Blockchain & Tokenization Development': cdnUrl(`${NAV_ICON_DIR}/blockchain-tokenization.svg`),
    'Crypto Exchange & Wallet Development': cdnUrl(`${NAV_ICON_DIR}/crypto-wallet.svg`),
    'Hire Developers': cdnUrl(`${NAV_ICON_DIR}/hire-developers.svg`),
    'Software Testing, QA & DevOps': cdnUrl(`${NAV_ICON_DIR}/qa-devops.svg`)
};

interface DropdownContext {
    asPath: string;
    close: () => void;
    onSelect: (index: number) => void;
    navType?: string;
}

const renderDropDownMenu = (
    menuLabel: React.ReactNode,
    menuItems: NavMenuGroup[] | undefined,
    selector: number,
    multiSubChild: boolean,
    updateSubMenu: (group: NavMenuGroup) => void,
    headingHoverSubMenu: NavMenuGroup | undefined,
    {asPath, close, onSelect, navType}: DropdownContext
) => {
    // Card links carry their group index in `link.number`; a missing one falls back to -1.
    const popperSideItem = (index?: number) => {
        close();
        onSelect(index ?? -1);
    };

    const singleItem = () => {
        close();
        onSelect(-1);
    };

    const hasCategories = (headingHoverSubMenu?.menuItems?.categories?.length ?? 0) > 0;

    return (
        <Fragment>
            <div className={`popover-sidebar-grid-wrapper ${styles.sidebarCol}`}>
                <div className={styles.gridContainer}>
                    <div className={`popover-menu-first ${styles.sidebarInnerItem}`}>
                        <div
                            className={cx(
                                'menu-main-heading-multiple outer-link-wrapper first-outer-link-wrapper',
                                multiSubChild ? styles.rail : styles.flatHeadingWrapper
                            )}
                        >
                            {multiSubChild ? (
                                menuItems?.map((item: NavMenuGroup, index: number) => {
                                    const heading = item.menuItems?.heading;
                                    const isActive = headingHoverSubMenu
                                        ? heading === headingHoverSubMenu?.menuItems?.heading
                                        : item.menuItems?.number === selector;
                                    const groupIconSrc = heading ? SIDEBAR_GROUP_ICONS[heading] : undefined;

                                    return (
                                        <button
                                            key={index}
                                            type="button"
                                            aria-pressed={isActive}
                                            onClick={() => popperSideItem(index)}
                                            onFocus={() => updateSubMenu(item)}
                                            onMouseEnter={() => updateSubMenu(item)}
                                            className={cx(
                                                'nav-multi-child-links',
                                                isActive ? 'selected-section' : 'not-selected-section',
                                                styles.sidebarItem,
                                                isActive && styles.sidebarItemActive
                                            )}
                                        >
                                            <div
                                                aria-hidden="true"
                                                className={cx('nav-sidebar-icon', styles.sidebarIcon, isActive && styles.sidebarIconActive)}
                                            >
                                                {groupIconSrc && (
                                                    <span
                                                        className={styles.sidebarGlyph}
                                                        style={{maskImage: `url(${groupIconSrc})`, WebkitMaskImage: `url(${groupIconSrc})`}}
                                                    />
                                                )}
                                            </div>
                                            <div
                                                className={cx(
                                                    'mui-header-menu-links',
                                                    styles.sidebarLabel,
                                                    isActive && styles.sidebarLabelActive
                                                )}
                                            >
                                                {heading}
                                            </div>
                                        </button>
                                    );
                                })
                            ) : (
                                <div className={`mui-header-menu-options single-menu-label outer-link-wrapper ${styles.singleMenuLabel}`}>
                                    {menuLabel} <Icon name="KeyboardArrowRight" />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className={cx('pop-menu-main-subchild-grid', styles.cardsCol, !hasCategories && styles.flatPanel)} data-lenis-prevent>
                <div className={styles.gridContainer}>
                    {hasCategories ? (
                        <div className={styles.categoryColumns}>
                            {renderCategoryColumns(
                                distributeIntoColumns(headingHoverSubMenu?.menuItems?.categories ?? [], CARD_COLUMNS),
                                headingHoverSubMenu?.menuItems?.heading ?? '',
                                asPath,
                                popperSideItem
                            )}
                        </div>
                    ) : (
                        menuItems?.map((item: NavMenuGroup, index: number) => (
                            <Fragment key={index}>
                                {!item.menuItems?.categories && (item.menuItems?.submenu?.length ?? 0) > 0 && navType === 'full-nav' ? (
                                    <div className={`outer-link-wrapper single-submenu-item-wrapper ${styles.flatItem}`}>
                                        {item.menuItems?.submenu?.map((element: NavSubmenuItem, itemIndex: number) => (
                                            <div key={itemIndex} className="menu-main-sub-heading-links">
                                                <Link href={element.path} target={element.target} onClick={() => singleItem()}>
                                                    <div className={`mui-header-menu-links ${styles.sublinks}`}>{element.heading}</div>
                                                </Link>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}

                                {item.menuItems?.multiple && item.menuItems.path ? (
                                    <div className={`outer-link-wrapper ${item.menuItems?.customClassName ?? ''} ${styles.flatItem}`}>
                                        <div className={`menu-main-heading-links new-menu-links ${styles.newMenuLinks}`}>
                                            <Link href={item.menuItems.path} onClick={() => close()}>
                                                <div className={`sublinks menu-text-with-arrow ${styles.sublinks}`}>
                                                    <span className={`nav-link-label ${styles.navLinkLabel}`}>
                                                        {item.menuItems?.heading}
                                                    </span>
                                                </div>
                                            </Link>
                                        </div>
                                    </div>
                                ) : null}
                            </Fragment>
                        ))
                    )}
                </div>
            </div>
            {hasCategories && (
                <div className={styles.ctaColumn}>
                    <DropdownCta onNavigate={() => popperSideItem(-1)} groupHeading={headingHoverSubMenu?.menuItems?.heading} />
                </div>
            )}
        </Fragment>
    );
};

interface HoverPopperPopupStateProps {
    popupId: string;
    btnClassName?: string;
    type?: 'button' | 'submit' | 'reset';
    btnLabel: React.ReactNode;
    menuData?: NavMenuGroup[];
    /** `null` marks a plain link item (no dropdown), e.g. "Portfolio". */
    path: string | null;
    navType?: string;
    multiSubChild: boolean;
    selector: number;
    onSelect: (index: number) => void;
}

const HoverPopperPopupState = (props: HoverPopperPopupStateProps) => {
    const {popupId, btnClassName, type = 'button', btnLabel, menuData, path, navType, multiSubChild, selector, onSelect} = props;
    const [headingHoverSubMenu, setHeadingHoverSubMenu] = useState(menuData?.[0]);
    const [isButtonHovered, setIsButtonHovered] = useState(false);
    const [isPopupHovered, setIsPopupHovered] = useState(false);
    // `isOpen` mirrors material-ui-popup-state: set by hovering the trigger, cleared when the
    // pointer leaves the trigger for anywhere but the panel, or when a link is chosen.
    const [isOpen, setIsOpen] = useState(false);
    // The panel (and its ~100 links) is only built after the first hover, as before.
    const [hasHovered, setHasHovered] = useState(false);
    const [panelTop, setPanelTop] = useState(0);
    const {asPath} = useRouter();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    const isDesktopNav = useMatchMedia(minWidthQuery(NAV_DESKTOP_MIN));

    const updateSubMenu = (newValue: NavMenuGroup) => {
        setHeadingHoverSubMenu(newValue);
    };

    const close = useCallback(() => setIsOpen(false), []);

    const handleButtonMouseEnter = (label: React.ReactNode) => {
        setIsButtonHovered(true);
        setIsOpen(true);
        if (!hasHovered) setHasHovered(true);
        if (label === 'Services') {
            if (selector !== -1) {
                setHeadingHoverSubMenu(menuData?.[selector]);
            } else {
                setHeadingHoverSubMenu(menuData?.[0]);
            }
        } else if (label === 'Products') {
            setHeadingHoverSubMenu(menuData?.[0]);
        } else if (label === 'Industries') {
            setHeadingHoverSubMenu(menuData?.[0]);
        }
    };

    const handleButtonMouseLeave = (event: React.MouseEvent) => {
        setIsButtonHovered(false);
        const next = event.relatedTarget;
        if (!(next instanceof Node && panelRef.current?.contains(next))) setIsOpen(false);
    };

    const handlePopupMouseEnter = () => {
        setIsPopupHovered(true);
        if (!hasHovered) setHasHovered(true);
    };

    const handlePopupMouseLeave = () => {
        setIsPopupHovered(false);
        if (selector !== -1) {
            setHeadingHoverSubMenu(menuData?.[selector]);
        } else {
            setHeadingHoverSubMenu(menuData?.[0]);
        }
    };

    const isUnderlineVisible = isButtonHovered || isPopupHovered;
    const isPanelVisible = hasHovered && isDesktopNav && isOpen && (isButtonHovered || isPopupHovered);

    // Keep the panel pinned under the trigger while it is open (the MUI Popper tracked
    // scroll and resize the same way).
    useIsomorphicLayoutEffect(() => {
        if (!isPanelVisible) return undefined;
        let frame = 0;
        const place = () => {
            const rect = buttonRef.current?.getBoundingClientRect();
            if (rect) setPanelTop(rect.bottom);
        };
        const schedule = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(place);
        };
        place();
        globalThis.addEventListener('scroll', schedule, {passive: true});
        globalThis.addEventListener('resize', schedule);
        return () => {
            cancelAnimationFrame(frame);
            globalThis.removeEventListener('scroll', schedule);
            globalThis.removeEventListener('resize', schedule);
        };
    }, [isPanelVisible]);

    const mainHeaderHover = (label: React.ReactNode) => {
        if (!hasHovered) setHasHovered(true);
        if (label === 'Services') {
            if (selector !== -1) {
                setHeadingHoverSubMenu(menuData?.[selector]);
            } else {
                setHeadingHoverSubMenu(menuData?.[0]);
            }
        } else if (label === 'Products') {
            onSelect(-1);
        }
    };

    return (
        <div>
            {path !== null && (
                <div className={styles.normalLinkWrapper}>
                    <div className={`menu-main-heading-links ${styles.normalLinkWrapper} ${styles.normalLinkInner}`}>
                        <Link href={path} onClick={() => onSelect(-1)}>
                            <div
                                className={`mui-header-menu-nromal-links nav-button-label ${styles.normalLink}`}
                                onMouseEnter={() => mainHeaderHover(btnLabel)}
                            >
                                {btnLabel}
                            </div>
                        </Link>
                    </div>
                </div>
            )}
            {path === null && (
                <>
                    <button
                        ref={buttonRef}
                        type={type}
                        aria-haspopup="true"
                        aria-controls={isOpen ? popupId : undefined}
                        className={cx(isUnderlineVisible ? 'navbar-underline mui-header-options-button' : btnClassName, styles.trigger)}
                        onMouseEnter={() => handleButtonMouseEnter(btnLabel)}
                        onTouchStart={() => handleButtonMouseEnter(btnLabel)}
                        onMouseLeave={handleButtonMouseLeave}
                    >
                        {btnLabel}
                    </button>

                    {isPanelVisible && (
                        <div
                            ref={panelRef}
                            id={popupId}
                            role="tooltip"
                            className={`popover-wrapper ${styles.popover}`}
                            style={{top: panelTop}}
                            onMouseEnter={handlePopupMouseEnter}
                            onMouseLeave={handlePopupMouseLeave}
                        >
                            <div className={`popover-paper ${styles.paper} ${styles.fade}`}>
                                <div className="popover-container-wrapper">
                                    <div className={styles.container}>
                                        <div className={`pop-navbar-grid-wrapper ${styles.row}`}>
                                            {renderDropDownMenu(
                                                btnLabel,
                                                menuData,
                                                selector,
                                                multiSubChild,
                                                updateSubMenu,
                                                headingHoverSubMenu,
                                                {asPath, close, onSelect, navType}
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export {styles as popNavMenuStyles};
export default memo(HoverPopperPopupState);
